/**
 * Pure column plan for the payment-report Excel export (PaymentReportTableRow's
 * handleExportReport). Kept separate from the component so the drop/relabel
 * logic — and the row-cell alignment it produces — can be unit tested without
 * rendering, mocking XLSX, or triggering a download.
 *
 * Rule for a non-taxable tenant (features.tax_enabled === false):
 *   - A column that is PURELY tax (its figure has no non-tax meaning, e.g.
 *     "Tax Type") is dropped entirely — `dropWhenNonTaxable: true`.
 *   - A column whose underlying figure still matters without tax (margin, net
 *     value per unit, total net value) is KEPT — for a non-taxable tenant the
 *     "excluding tax" value already equals the plain value, and no other kept
 *     column carries the same number — but its header drops the tax wording
 *     via `nonTaxableHeader`.
 *
 * For a taxable tenant every column is kept with its original header — the
 * taxable output is byte-identical to before this file existed.
 */
export interface PaymentReportColumn {
  /** The report row's raw field name (what `row[key]` reads). */
  key: string
  /** Header text when the tenant is taxable (unchanged from the original export). */
  taxableHeader: string
  /** Header text to use instead, for a non-taxable tenant, when the column is kept. Omit if identical to `taxableHeader`. */
  nonTaxableHeader?: string
  /** True ⇒ the column is removed entirely for a non-taxable tenant (purely tax, or duplicated by another kept column). */
  dropWhenNonTaxable: boolean
}

export const PAYMENT_REPORT_COLUMNS: PaymentReportColumn[] = [
  {key: 'name', taxableHeader: 'Name', dropWhenNonTaxable: false},
  {key: 'barcode', taxableHeader: 'Barcode', dropWhenNonTaxable: false},
  {key: 'tax_type', taxableHeader: 'Tax Type', dropWhenNonTaxable: true},
  {key: 'transaction_type', taxableHeader: 'Transaction Type', dropWhenNonTaxable: false},
  {key: 'quantity', taxableHeader: 'Quantity', dropWhenNonTaxable: false},
  {key: 'margin_exclude_tax', taxableHeader: 'Margin (Excl. Tax)', nonTaxableHeader: 'Margin', dropWhenNonTaxable: false},
  {key: 'net_value_per_unit_exclude_tax', taxableHeader: 'Net Value/Unit (Excl. Tax)', nonTaxableHeader: 'Net Value/Unit', dropWhenNonTaxable: false},
  {key: 'total_net_value_exclude_tax', taxableHeader: 'Total Net Value (Excl. Tax)', nonTaxableHeader: 'Total Net Value', dropWhenNonTaxable: false},
]

export interface PaymentReportColumnPlan {
  /** Row field names to read, in export column order — index-aligned with `headers`. */
  keys: string[]
  /** Header cells to print, in export column order — index-aligned with `keys`. */
  headers: string[]
}

/**
 * Resolves which columns survive and what to print for their header, for the
 * given tenant. Taxable ⇒ every column, original headers (byte-identical to
 * the pre-existing export). Non-taxable ⇒ tax-only columns dropped, the rest
 * kept with their tax wording stripped from the header.
 */
export function buildPaymentReportColumnPlan(taxEnabled: boolean): PaymentReportColumnPlan {
  const kept = PAYMENT_REPORT_COLUMNS.filter((col) => taxEnabled || !col.dropWhenNonTaxable)
  return {
    keys: kept.map((col) => col.key),
    headers: kept.map((col) => (taxEnabled ? col.taxableHeader : col.nonTaxableHeader ?? col.taxableHeader)),
  }
}
